import { DEFAULT_TIMEZONE, localDateKey, type ResolvedOccurrence, toZoned } from "@dream/domain";
import { IconGoogle, Skeleton } from "@dream/ui";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import {
  type EventHoverModel,
  googleHoverModelFromEvent,
} from "../components/event-hover-card.tsx";
import { TypeIcon } from "../components/type-icon.tsx";
import { chipCssVars } from "../lib/chip-contrast.ts";
import type {
  CalendarCommand,
  ClientMeta,
  GoogleOverlayEvent,
  TypeMeta,
} from "./adapter/mapping.ts";
import { DAY_MS, layoutTimelineItems } from "./timeline-layout.ts";

/**
 * Timeline operacional.
 *
 * Implementação própria, e não `@fullcalendar/resource-timeline`: aquele plugin
 * é FullCalendar Premium, com licença comercial, e o briefing restringe o
 * projeto a plugins gratuitos. A auditoria está em docs/repository-research.md.
 *
 * Layout: uma faixa por grupo (cliente, projeto ou responsável). Entregas de
 * um dia vivem na coluna do dia (CSS Grid); barras de vários dias atravessam
 * as colunas. A grade rola por dentro do calendário, nunca na página.
 */

export interface OperationalTimelineProps {
  occurrences: ResolvedOccurrence[];
  types: TypeMeta[];
  clients: ClientMeta[];
  timezone: string;
  focusDate: Date;
  groupBy: "client" | "project" | "assignee";
  interactive: boolean;
  loading?: boolean;
  onCommand: (command: CalendarCommand) => void;
  /** Quantidade de dias exibidos. Padrão: 14. */
  days?: number;
  projects?: Array<{ id: string; name: string; color: string }>;
  team?: Array<{ id: string; name: string }>;
  onHover?: (model: EventHoverModel) => void;
  onHoverLeave?: () => void;
  googleEvents?: GoogleOverlayEvent[];
}

interface Lane {
  key: string;
  label: string;
  color: string;
  items: ResolvedOccurrence[];
}

export function OperationalTimeline({
  occurrences,
  types,
  clients,
  timezone = DEFAULT_TIMEZONE,
  focusDate,
  groupBy,
  interactive,
  loading = false,
  onCommand,
  days = 14,
  projects = [],
  team = [],
  onHover,
  onHoverLeave,
  googleEvents = [],
}: OperationalTimelineProps) {
  const typeMap = useMemo(() => new Map(types.map((t) => [t.id, t])), [types]);
  const clientMap = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const projectMap = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const teamMap = useMemo(() => new Map(team.map((m) => [m.id, m])), [team]);

  // A janela começa na meia-noite local do dia de referência, para que as
  // colunas correspondam a dias de calendário e não a fatias de 24h a partir
  // de um instante arbitrário.
  const window = useMemo(() => {
    const start = DateTime.fromJSDate(focusDate, { zone: timezone }).startOf("day");
    return { start, end: start.plus({ days }), totalMs: days * 86_400_000 };
  }, [focusDate, timezone, days]);

  const dayColumns = useMemo(
    () => Array.from({ length: days }, (_, i) => window.start.plus({ days: i })),
    [window, days],
  );

  const lanes = useMemo<Lane[]>(() => {
    const map = new Map<string, Lane>();

    for (const occurrence of occurrences) {
      let key: string;
      let label: string;
      let color: string;

      if (groupBy === "client") {
        key = occurrence.clientId;
        const client = clientMap.get(occurrence.clientId);
        label = client?.name ?? "Cliente";
        color = client?.color ?? "#0074FF";
      } else if (groupBy === "project") {
        key = occurrence.projectId ?? "__sem_projeto__";
        const project =
          occurrence.projectId === null ? undefined : projectMap.get(occurrence.projectId);
        label = project?.name ?? "Sem projeto";
        color = project?.color ?? "#94a3b8";
      } else {
        key = occurrence.assigneeId ?? "__sem_responsavel__";
        label =
          occurrence.assigneeId === null
            ? "Sem responsável"
            : (teamMap.get(occurrence.assigneeId)?.name ?? "Responsável");
        color = "#0074FF";
      }

      const lane = map.get(key) ?? { key, label, color, items: [] };
      lane.items.push(occurrence);
      map.set(key, lane);
    }

    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [occurrences, groupBy, clientMap, projectMap, teamMap]);

  if (loading && occurrences.length === 0 && googleEvents.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-9 w-40 shrink-0" />
            <Skeleton className="h-9 flex-1" />
          </div>
        ))}
      </div>
    );
  }

  const todayKey = localDateKey(new Date(), timezone);
  const groupLabel =
    groupBy === "client" ? "Cliente" : groupBy === "project" ? "Projeto" : "Responsável";

  return (
    <div className="dream-timeline dream-scroll min-h-0 flex-1">
      <div
        className="dream-timeline__canvas"
        style={{ ["--dream-timeline-days" as string]: String(days) }}
      >
        <div className="dream-timeline__head flex">
          <div className="dream-timeline__label px-2.5 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
            {groupLabel}
          </div>
          <div className="dream-timeline__days flex">
            {dayColumns.map((day) => {
              const key = day.toFormat("yyyy-MM-dd");
              const isToday = key === todayKey;
              const isWeekend = day.weekday === 6 || day.weekday === 7;
              return (
                <div
                  key={key}
                  data-today={isToday ? "true" : undefined}
                  data-weekend={isWeekend ? "true" : undefined}
                  className="dream-timeline__day px-0.5 py-2 text-center"
                >
                  <div className="whitespace-nowrap text-[0.625rem] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                    {day.setLocale("pt-BR").toFormat("ccc").replace(".", "")}
                  </div>
                  <div
                    className={[
                      "mx-auto mt-0.5 flex size-7 items-center justify-center rounded-full text-[0.8125rem] font-bold tabular-nums",
                      isToday ? "bg-[#0074FF] text-white" : "text-[var(--color-ink)]",
                    ].join(" ")}
                  >
                    {day.toFormat("dd")}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {lanes.length === 0 && googleEvents.length === 0
          ? null
          : lanes.map((lane) => (
              <div key={lane.key} className="dream-timeline__lane flex">
                <div className="dream-timeline__label flex items-center gap-2 px-2.5 py-2">
                  <span
                    aria-hidden="true"
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: lane.color }}
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium text-[var(--color-ink)]"
                    title={lane.label}
                  >
                    {lane.label}
                  </span>
                  <span className="dream-timeline__count shrink-0 text-[0.6875rem] tabular-nums text-[var(--color-ink-muted)]">
                    {lane.items.length}
                  </span>
                </div>

                <div className="dream-timeline__days relative">
                  <div className="pointer-events-none absolute inset-0 flex" aria-hidden="true">
                    {dayColumns.map((day) => {
                      const key = day.toFormat("yyyy-MM-dd");
                      const isWeekend = day.weekday === 6 || day.weekday === 7;
                      return (
                        <div
                          key={key}
                          data-today={key === todayKey ? "true" : undefined}
                          data-weekend={isWeekend ? "true" : undefined}
                          className="dream-timeline__day"
                        />
                      );
                    })}
                  </div>

                  <TimelineBars
                    items={lane.items}
                    window={window}
                    timezone={timezone}
                    typeMap={typeMap}
                    clientMap={clientMap}
                    projectMap={projectMap}
                    interactive={interactive}
                    onCommand={onCommand}
                    onHover={onHover}
                    onHoverLeave={onHoverLeave}
                  />
                </div>
              </div>
            ))}
        {googleEvents.length > 0 ? (
          <GoogleTimelineLane
            events={googleEvents}
            rangeWindow={window}
            timezone={timezone}
            onHover={onHover}
            onHoverLeave={onHoverLeave}
          />
        ) : null}
      </div>
    </div>
  );
}

function TimelineBars({
  items,
  window,
  timezone,
  typeMap,
  clientMap,
  projectMap,
  interactive,
  onCommand,
  onHover,
  onHoverLeave,
}: {
  items: ResolvedOccurrence[];
  window: { start: DateTime; end: DateTime; totalMs: number };
  timezone: string;
  typeMap: Map<string, TypeMeta>;
  clientMap: Map<string, ClientMeta>;
  projectMap: Map<string, { id: string; name: string; color: string }>;
  interactive: boolean;
  onCommand: (command: CalendarCommand) => void;
  onHover?: (model: EventHoverModel) => void;
  onHoverLeave?: () => void;
}) {
  const [expandedDays, setExpandedDays] = useState<Set<number>>(() => new Set());
  const days = Math.round(window.totalMs / DAY_MS);

  const placed = useMemo(
    () =>
      layoutTimelineItems(
        items.map((occurrence) => ({
          id: occurrence.occurrenceId,
          startMs: occurrence.startsAt.getTime(),
          endMs: occurrence.endsAt.getTime(),
          isAllDay: occurrence.isAllDay,
        })),
        window.start.toMillis(),
        days,
        { expandedDays },
      ),
    [items, window, days, expandedDays],
  );

  const byId = useMemo(() => new Map(items.map((item) => [item.occurrenceId, item])), [items]);
  const rowCount = placed.reduce((max, item) => Math.max(max, item.row + 1), 1);

  return (
    <div
      className="dream-timeline__bars relative w-full"
      style={{
        gridTemplateColumns: `repeat(${days}, minmax(0, 1fr))`,
        gridAutoRows: "2.05rem",
        minHeight: `${rowCount * 2.05 + 0.7}rem`,
      }}
    >
      {placed.map((item) => {
        const gridStyle = {
          gridColumn: `${item.colStart} / span ${item.colSpan}`,
          gridRow: item.row + 1,
          ["--dream-stagger" as string]: `${Math.min(item.row, 8) * 18}ms`,
        };

        if (item.kind === "overflow") {
          const dayIndex = item.dayIndex ?? 0;
          return (
            <button
              key={item.id}
              type="button"
              data-testid="timeline-overflow"
              aria-label={`Mostrar mais ${item.overflowCount} entregas neste dia`}
              onClick={() =>
                setExpandedDays((current) => {
                  const next = new Set(current);
                  next.add(dayIndex);
                  return next;
                })
              }
              className="dream-timeline-bar dream-timeline-bar--more"
              style={gridStyle}
            >
              +{item.overflowCount}
            </button>
          );
        }

        const occurrence = byId.get(item.id);
        if (occurrence === undefined) return null;
        const type = typeMap.get(occurrence.typeId);
        const color = type?.color ?? "#0074FF";
        const start = toZoned(occurrence.startsAt, timezone);
        const end = toZoned(occurrence.endsAt, timezone);

        const label = occurrence.isAllDay
          ? `${start.setLocale("pt-BR").toFormat("dd/MM")} — dia inteiro`
          : `${start.toFormat("dd/MM HH:mm")} até ${end.toFormat("dd/MM HH:mm")}`;

        return (
          <button
            key={occurrence.occurrenceId}
            type="button"
            aria-label={`${occurrence.title}, ${label}${occurrence.isLate ? ", atrasada" : ""}`}
            onMouseEnter={(event) => {
              if (onHover === undefined) return;
              const rect = event.currentTarget.getBoundingClientRect();
              onHover({
                title: occurrence.title,
                typeLabel: type?.label ?? null,
                typeSlug: type?.slug ?? null,
                typeIcon: type?.icon ?? null,
                color,
                clientName: clientMap.get(occurrence.clientId)?.name ?? null,
                projectName:
                  occurrence.projectId === null
                    ? null
                    : (projectMap.get(occurrence.projectId)?.name ?? null),
                status: occurrence.status,
                isLate: occurrence.isLate,
                isRecurring: occurrence.isRecurring,
                visibleToClient: occurrence.visibleToClient,
                hasPublish: occurrence.publishAt !== null,
                description: occurrence.description,
                when: label,
                rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
              });
            }}
            onMouseLeave={() => onHoverLeave?.()}
            onClick={() =>
              onCommand({
                kind: "open",
                deliverableId: occurrence.deliverableId,
                occurrenceStart: occurrence.occurrenceStart.toISOString(),
                title: occurrence.title,
              })
            }
            className={[
              "dream-timeline-bar",
              item.colSpan === 1 ? "dream-timeline-bar--day" : "dream-timeline-bar--span",
              item.clippedStart ? "dream-timeline-bar--clip-start" : "",
              item.clippedEnd ? "dream-timeline-bar--clip-end" : "",
              interactive ? "cursor-pointer" : "cursor-default",
              occurrence.status === "cancelled" ? "line-through opacity-60" : "",
            ].join(" ")}
            style={{
              ...gridStyle,
              ...chipCssVars(color),
              backgroundColor: color,
              outline: occurrence.isLate ? "1px dashed var(--dream-danger-fg)" : "none",
              outlineOffset: "-1px",
            }}
          >
            {item.clippedStart && (
              <span aria-hidden="true" className="dream-timeline-bar__caret">
                ◂
              </span>
            )}
            <span className="dream-timeline-bar__icon" aria-hidden="true">
              <TypeIcon slug={type?.slug} name={type?.icon} size={12} />
            </span>
            <span className="dream-timeline-bar__title">{occurrence.title}</span>
            {occurrence.isLate && (
              <span aria-hidden="true" className="dream-timeline-bar__late">
                !
              </span>
            )}
            {item.clippedEnd && (
              <span aria-hidden="true" className="dream-timeline-bar__caret">
                ▸
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function GoogleTimelineLane({
  events,
  rangeWindow,
  timezone,
  onHover,
  onHoverLeave,
}: {
  events: GoogleOverlayEvent[];
  rangeWindow: { start: DateTime; end: DateTime; totalMs: number };
  timezone: string;
  onHover?: (model: EventHoverModel) => void;
  onHoverLeave?: () => void;
}) {
  const [expandedDays, setExpandedDays] = useState<Set<number>>(() => new Set());
  const days = Math.round(rangeWindow.totalMs / DAY_MS);
  const placed = useMemo(
    () =>
      layoutTimelineItems(
        events.map((event) => ({
          id: event.id,
          startMs: Date.parse(event.startsAt),
          endMs: Date.parse(event.endsAt),
          isAllDay: event.isAllDay,
        })),
        rangeWindow.start.toMillis(),
        days,
        { expandedDays },
      ),
    [events, rangeWindow, days, expandedDays],
  );
  const byId = useMemo(() => new Map(events.map((event) => [event.id, event])), [events]);
  const rowCount = placed.reduce((max, item) => Math.max(max, item.row + 1), 1);
  const todayKey = localDateKey(new Date(), timezone);

  return (
    <div className="dream-timeline__lane flex" data-source="google">
      <div className="dream-timeline__label flex items-center gap-2 px-2.5 py-2">
        <span
          aria-hidden="true"
          className="flex size-4 shrink-0 items-center justify-center text-[#4285F4]"
        >
          <IconGoogle className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium text-[var(--color-ink)]">
          Google Agenda
        </span>
        <span className="dream-timeline__count shrink-0 text-[0.6875rem] tabular-nums text-[var(--color-ink-muted)]">
          {events.length}
        </span>
      </div>
      <div className="dream-timeline__days relative">
        <div className="pointer-events-none absolute inset-0 flex" aria-hidden="true">
          {Array.from({ length: days }, (_, index) => {
            const day = rangeWindow.start.plus({ days: index });
            const key = day.toFormat("yyyy-MM-dd");
            const isWeekend = day.weekday === 6 || day.weekday === 7;
            return (
              <div
                key={key}
                data-today={key === todayKey ? "true" : undefined}
                data-weekend={isWeekend ? "true" : undefined}
                className="dream-timeline__day"
              />
            );
          })}
        </div>
        <div
          className="dream-timeline__bars relative w-full"
          style={{
            gridTemplateColumns: `repeat(${days}, minmax(0, 1fr))`,
            gridAutoRows: "2.05rem",
            minHeight: `${rowCount * 2.05 + 0.7}rem`,
          }}
        >
          {placed.map((item) => {
            const gridStyle = {
              gridColumn: `${item.colStart} / span ${item.colSpan}`,
              gridRow: item.row + 1,
              ["--dream-stagger" as string]: `${Math.min(item.row, 8) * 18}ms`,
            };
            if (item.kind === "overflow") {
              const dayIndex = item.dayIndex ?? 0;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid="timeline-google-overflow"
                  aria-label={`Mostrar mais ${item.overflowCount} eventos da Agenda neste dia`}
                  onClick={() =>
                    setExpandedDays((current) => {
                      const next = new Set(current);
                      next.add(dayIndex);
                      return next;
                    })
                  }
                  className="dream-timeline-bar dream-timeline-bar--more"
                  style={gridStyle}
                >
                  +{item.overflowCount}
                </button>
              );
            }
            const event = byId.get(item.id);
            if (event === undefined) return null;
            const start = DateTime.fromISO(event.startsAt, { zone: timezone });
            const end = DateTime.fromISO(event.endsAt, { zone: timezone });
            const timeText = event.isAllDay
              ? "Dia inteiro"
              : `${start.toFormat("HH:mm")}–${end.toFormat("HH:mm")}`;
            return (
              <button
                key={event.id}
                type="button"
                aria-label={`${event.title}, ${timeText}, Google Agenda`}
                onMouseEnter={(hoverEvent) => {
                  if (onHover === undefined) return;
                  onHover(
                    googleHoverModelFromEvent({
                      title: event.title,
                      props: {
                        source: "google",
                        htmlLink: event.htmlLink,
                        calendarName: event.calendarName,
                        location: event.location,
                        hangoutLink: event.hangoutLink,
                        attendees: event.attendees,
                        transparency: event.transparency,
                        eventType: event.eventType,
                        colorId: event.colorId,
                        foreground: event.foreground,
                        originalStartsAt: event.startsAt,
                        originalEndsAt: event.endsAt,
                        originalAllDay: event.isAllDay,
                        sortMs: Date.parse(event.startsAt),
                      },
                      color: event.color,
                      allDay: event.isAllDay,
                      timeText,
                      el: hoverEvent.currentTarget,
                    }),
                  );
                }}
                onMouseLeave={() => onHoverLeave?.()}
                onClick={() => {
                  if (event.htmlLink !== null && event.htmlLink !== "") {
                    window.open(event.htmlLink, "_blank", "noopener,noreferrer");
                  }
                }}
                className={[
                  "dream-timeline-bar dream-timeline-bar--google",
                  item.colSpan === 1 ? "dream-timeline-bar--day" : "dream-timeline-bar--span",
                  item.clippedStart ? "dream-timeline-bar--clip-start" : "",
                  item.clippedEnd ? "dream-timeline-bar--clip-end" : "",
                  "cursor-pointer",
                ].join(" ")}
                style={{
                  ...gridStyle,
                  ...chipCssVars(event.color, event.foreground),
                  backgroundColor: event.color,
                }}
              >
                {item.clippedStart && (
                  <span aria-hidden="true" className="dream-timeline-bar__caret">
                    ◂
                  </span>
                )}
                <span className="dream-timeline-bar__icon" aria-hidden="true">
                  <IconGoogle className="size-3" />
                </span>
                <span className="dream-timeline-bar__title">{event.title}</span>
                {item.clippedEnd && (
                  <span aria-hidden="true" className="dream-timeline-bar__caret">
                    ▸
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
